/**
 * CovenantBadge — the covenant "attention" chip (severity badges feature).
 *
 * Council rails: the chip shows the covenant NAME (factual) in the tier colour
 * with an icon (colour is never the only cue); the tier word and the "what this
 * binds" explanation live only in the hover. No green. Routine → renders null
 * (no badge — absence already says routine). Critical uses the tinted error
 * treatment (no new token).
 */
import { AlertTriangle, AlertCircle, Lock } from 'react-feather';
import { clsx } from 'clsx';

import { Tooltip } from '../../ui/Tooltip';
import type { CovenantTier } from '../../../lib/covenantSeverity';

interface CovenantBadgeProps {
  tier: CovenantTier;
  /** The covenant's display name — the visible chip text (factual). */
  label: string;
  /** Descriptive hover blurb ("what this binds"). */
  blurb: string;
  /** Optional deal-specific clause text, shown under the blurb on hover. */
  clause?: string | null;
}

const TIER_STYLE = {
  critical: { cls: 'text-error bg-error/10 border-error/40', Icon: AlertTriangle },
  high: { cls: 'text-warning bg-warning/10 border-warning/30', Icon: AlertCircle },
  moderate: { cls: 'text-txt-secondary bg-surface-subtle border-border-strong', Icon: Lock },
} as const;

export function CovenantBadge({ tier, label, blurb, clause }: CovenantBadgeProps) {
  if (tier === 'routine') {return null;} // silent — no badge for routine/unknown
  const { cls, Icon } = TIER_STYLE[tier];
  const content = clause ? `${blurb}\n\n"${clause}"` : blurb;
  return (
    <Tooltip content={content} position="top">
      <span
        className={clsx(
          'inline-flex items-center gap-1.5 border px-2 py-0.5 text-body-sm font-medium',
          cls,
        )}
      >
        <Icon size={12} className="flex-shrink-0" aria-hidden />
        {label}
      </span>
    </Tooltip>
  );
}
